import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, unwrap, unwrapEmpty } from "./client";
import type { Me, Permission } from "./types";

export const meQueryKey = ["me"] as const;

/** Přihlášený uživatel; null = nepřihlášen (401). */
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async (): Promise<Me | null> => {
      try {
        return unwrap(await api.GET("/api/v1/auth/me"));
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

/** Přihlášený uživatel v chráněné části aplikace (RequireAuth zaručuje, že existuje). */
export function useCurrentUser(): Me {
  const { data } = useMe();
  if (!data) throw new Error("useCurrentUser mimo přihlášenou část aplikace");
  return data;
}

export function useHasPermission(permission: Permission): boolean {
  const { data } = useMe();
  return data?.permissions.includes(permission) ?? false;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { login: string; password: string }) =>
      unwrapEmpty(await api.POST("/api/v1/auth/login", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meQueryKey }),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrapEmpty(await api.POST("/api/v1/auth/logout")),
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(meQueryKey, null);
    },
  });
}

export function usePasswordLinkInfo(userId: string | null, token: string | null) {
  return useQuery({
    queryKey: ["password-link", userId, token],
    enabled: !!userId && !!token,
    retry: false,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/auth/password-link", { params: { query: { userId: userId!, token: token! } } })),
  });
}

export function useSetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { userId: string; token: string; password: string }) =>
      unwrapEmpty(await api.POST("/api/v1/auth/set-password", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meQueryKey }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) =>
      unwrapEmpty(await api.POST("/api/v1/auth/me/password", { body })),
  });
}

export function useChangeLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (language: string) => unwrapEmpty(await api.PUT("/api/v1/auth/me/language", { body: { language } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meQueryKey }),
  });
}
